import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { adminApi as configureApi } from '@/features/user/configureApi';
import { adminApi as adminConfigureApi } from '@/features/admin/components/adminApi';
import type { Category } from '@/types/category';
import type { Country } from '@/types/country';
import type { Topic } from '@/types/topic';

const FIFTEEN_MINUTES = 15 * 60 * 1000;

export const configureQueryKeys = {
  countries: ['configure', 'countries'] as const,
  topics: ['configure', 'topics'] as const,
  categoriesByCountry: (countryCode: string) =>
    ['configure', 'categories', countryCode.toUpperCase()] as const,
  adminCountries: ['configure', 'admin', 'countries'] as const,
  adminTopics: ['configure', 'admin', 'topics'] as const,
  adminCategories: ['configure', 'admin', 'categories'] as const,
  adminCategoriesByCountry: (countryCode: string) =>
    ['configure', 'admin', 'categories', countryCode.toUpperCase()] as const,
};

export function useCountriesQuery() {
  return useQuery<Array<Country>>({
    queryKey: configureQueryKeys.countries,
    queryFn: async () => (await configureApi.getAllCountries()) || [],
    staleTime: FIFTEEN_MINUTES,
  });
}

export function useTopicsQuery() {
  return useQuery<Array<Topic>>({
    queryKey: configureQueryKeys.topics,
    queryFn: async () => (await configureApi.getAllTopics()) || [],
    staleTime: FIFTEEN_MINUTES,
  });
}

export function useCategoriesByCountryQueries(countries: Array<Country>) {
  const queries = useMemo(
    () =>
      countries.map(country => ({
        queryKey: configureQueryKeys.categoriesByCountry(country.countryCode),
        queryFn: async () =>
          ((await configureApi.getAllCategoriesByCountry(country.countryCode)) ||
            []) as Array<Category>,
        staleTime: FIFTEEN_MINUTES,
      })),
    [countries]
  );

  return useQueries({ queries });
}

export function useCategoriesByCountryQuery(countryCode?: string, enabled = true) {
  const normalized = (countryCode || '').toUpperCase();
  return useQuery<Array<Category>>({
    queryKey: configureQueryKeys.categoriesByCountry(normalized || 'UNKNOWN'),
    queryFn: async () =>
      ((await configureApi.getAllCategoriesByCountry(normalized)) || []) as Array<Category>,
    staleTime: FIFTEEN_MINUTES,
    enabled: Boolean(normalized) && enabled,
  });
}

export function useTopicsAsCategoriesQuery(enabled = true) {
  return useQuery<Array<Category>>({
    queryKey: ['configure', 'topics-as-categories'],
    queryFn: async () => {
      const topics = (await configureApi.getAllTopics()) || [];
      return topics.map(
        (topic: { topicCode: string; topicNameEN: string; topicNameVI?: string }) => ({
          categoryCode: topic.topicCode,
          categoryNameEN: topic.topicNameEN,
          categoryNameVI: topic.topicNameVI || topic.topicNameEN,
        })
      );
    },
    staleTime: FIFTEEN_MINUTES,
    enabled,
  });
}

export function useAdminCountriesQuery() {
  return useQuery<Array<Country>>({
    queryKey: configureQueryKeys.adminCountries,
    queryFn: async () => (await adminConfigureApi.getAllCountries()) || [],
    staleTime: FIFTEEN_MINUTES,
  });
}

export function useAdminTopicsQuery() {
  return useQuery<Array<Topic>>({
    queryKey: configureQueryKeys.adminTopics,
    queryFn: async () => (await adminConfigureApi.getAllTopics()) || [],
    staleTime: FIFTEEN_MINUTES,
  });
}

export function useAdminCategoriesQuery() {
  return useQuery<Array<Category>>({
    queryKey: configureQueryKeys.adminCategories,
    queryFn: async () => (await adminConfigureApi.getAllCategories()) || [],
    staleTime: FIFTEEN_MINUTES,
  });
}

export function useAdminCategoriesByCountryQuery(countryCode?: string, enabled = true) {
  const normalized = (countryCode || '').toUpperCase();
  return useQuery<Array<Category>>({
    queryKey: configureQueryKeys.adminCategoriesByCountry(normalized || 'UNKNOWN'),
    queryFn: async () =>
      ((await adminConfigureApi.getAllCategoriesByCountry(normalized)) || []) as Array<Category>,
    staleTime: FIFTEEN_MINUTES,
    enabled: Boolean(normalized) && enabled,
  });
}

export function useAdminTopicsAsCategoriesQuery(enabled = true) {
  return useQuery<Array<Category>>({
    queryKey: ['configure', 'admin', 'topics-as-categories'],
    queryFn: async () => {
      const topics = (await adminConfigureApi.getAllTopics()) || [];
      return topics.map(
        (topic: { topicCode: string; topicNameEN: string; topicNameVI?: string }) => ({
          categoryCode: topic.topicCode,
          categoryNameEN: topic.topicNameEN,
          categoryNameVI: topic.topicNameVI || topic.topicNameEN,
        })
      );
    },
    staleTime: FIFTEEN_MINUTES,
    enabled,
  });
}
